import { API_BASE, messageFromErrorBody } from './authApi';
import { authHeaders } from './authStorage';

const jsonHeaders = () => ({
  'Content-Type': 'application/json',
  Accept: 'application/json',
  ...authHeaders(),
});

export function mapDecisionFromApi(d) {
  return {
    id: d.id,
    title: d.title ?? '',
    description: d.description ?? '',
    branchId: d.companyId,
    branch: d.companyName ?? '',
    decisionTypeId: d.decisionTypeId ? String(d.decisionTypeId) : '',
    decisionType: d.decisionTypeName ?? '',
    committeeId: d.committeeId ? String(d.committeeId) : '',
    committee: d.committeeName ?? '',
    creator: d.creator ?? 'الرئيس التنفيذي',
    date: d.createdAt ? String(d.createdAt).slice(0, 10) : '',
    status: d.status ?? 'pending',
    priority: d.priority ?? 'medium',
    deadline: d.deadline ? String(d.deadline).slice(0, 10) : '',
    totalMembers: d.totalMembers ?? 0,
    attachments: Array.isArray(d.attachments)
      ? d.attachments.map((a) => ({
        id: a.id,
        name: a.name,
        type: a.type ?? 'pdf',
        size: a.size ?? '—',
        ...(a.storedFileName ? { storedFileName: a.storedFileName } : {}),
      }))
      : [],
  };
}

export function mapDecisionFormToPayload(form) {
  return {
    title: form.title?.trim() ?? '',
    description: form.description?.trim() || null,
    companyId: Number(form.branchId),
    decisionTypeId: Number(form.decisionTypeId),
    committeeId: form.committeeId ? Number(form.committeeId) : null,
    status: form.status ?? 'pending',
    priority: form.priority ?? 'medium',
    deadline: form.deadline || null,
    totalMembers: Number(form.totalMembers || 0),
    attachments: (form.attachments || [])
      .filter((a) => !a?.__file)
      .map(({ id, name, type, size, storedFileName }) => ({
        id: id || `a-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        name,
        type: type || 'pdf',
        size: size || '—',
        ...(storedFileName ? { storedFileName } : {}),
      })),
  };
}

export async function fetchDecisions() {
  const res = await fetch(`${API_BASE}/api/decisions`, { headers: { Accept: 'application/json', ...authHeaders() } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(messageFromErrorBody(data, 'تعذر تحميل القرارات'));
  const list = Array.isArray(data) ? data : [];
  return list.map(mapDecisionFromApi);
}

export async function fetchDecisionById(id) {
  const res = await fetch(`${API_BASE}/api/decisions/${id}/detail`, { headers: { Accept: 'application/json', ...authHeaders() } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(messageFromErrorBody(data, 'تعذر تحميل القرار'));
  return mapDecisionFromApi(data);
}

export async function createDecision(payload) {
  const res = await fetch(`${API_BASE}/api/decisions`, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(payload) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(messageFromErrorBody(data, 'تعذر إضافة القرار'));
  return mapDecisionFromApi(data);
}

export async function updateDecision(id, payload) {
  const res = await fetch(`${API_BASE}/api/decisions/${id}/edit`, { method: 'PUT', headers: jsonHeaders(), body: JSON.stringify(payload) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(messageFromErrorBody(data, 'تعذر حفظ القرار'));
  return mapDecisionFromApi(data);
}

export async function deleteDecision(id) {
  const res = await fetch(`${API_BASE}/api/decisions/${id}`, { method: 'DELETE', headers: { Accept: 'application/json', ...authHeaders() } });
  if (res.status === 204) return;
  const data = await res.json().catch(() => ({}));
  throw new Error(messageFromErrorBody(data, 'تعذر حذف القرار'));
}

export async function uploadDecisionAttachmentFiles(decisionId, files) {
  const fd = new FormData();
  Array.from(files).forEach((file) => fd.append('files', file));
  const res = await fetch(`${API_BASE}/api/decisions/${decisionId}/attachments`, {
    method: 'POST',
    headers: { ...authHeaders() },
    body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(messageFromErrorBody(data, 'تعذر رفع الملفات'));
  }
  return mapDecisionFromApi(data);
}

export function decisionAttachmentDownloadUrl(decisionId, attachmentId) {
  return `${API_BASE}/api/decisions/${decisionId}/attachments/file/${encodeURIComponent(attachmentId)}`;
}

/**
 * @param {number} id
 * @param {'all' | 'board' | 'shareholders'} [audience]
 */
export async function sendDecisionWhatsappNotifications(id, audience = 'all') {
  const res = await fetch(`${API_BASE}/api/decisions/${id}/whatsapp-notify`, {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ audience }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(messageFromErrorBody(data, 'تعذر إشعار واتساب'));
  return data;
}
